import { useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import Dialog from '../../../components/Dialog';
import Text from '../../../components/Text';
import CustomDateTimePicker from '../../../components/DatePicker/CustomDateTimePicker';
import { Checkbox } from '../../DutyAssign/components/Attendance/Setting';
import StyleInput from '../../../components/StyleInput';
import { CheckIcon, CloseIcon, SeeIcon } from '../../../components/Icon';
import { patientSurveyAPI } from '../../../apis';
import { downloadFileById } from '../../../utils';
import { i18n } from '../../../language';

const dateFormat = 'YYYY-MM-DD';

const ExportSurveyPopup = ({ handleClose, filterWardLocation }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({
    endDate: moment(),
    filePassword: '',
    startDate: moment().add(-1, 'months'),
    types: ['Appreciation', 'Feedback', 'Complaint'],
  });
  const [verify, setVerify] = useState({
    isRequired: 'pending',
    uppercaseLetters: 'pending',
    lowercaseLetters: 'pending',
    numerals: 'pending',
  });
  const [passwordType, setPasswordType] = useState('password');
  const lang = i18n.locale === 'en_US';
  // const lang = i18n.locale === 'en-us' ? 'en_US' : 'zh_CN';

  const handleExport = async () => {
    try {
      const isVerifyed = await checkRequirements(formData.filePassword);
      if (isVerifyed) {
        const params = {
          ...formData,
          ...filterWardLocation,
          startDate: moment(formData.startDate).hours(0).minutes(0).seconds(0).format('YYYY-MM-DD HH:mm:ss'),
          endDate: moment(formData.endDate).hours(23).minutes(59).seconds(59).format('YYYY-MM-DD HH:mm:ss'),
          lang,
        };
        await downloadFileById(
          () => {
            return patientSurveyAPI.getPatientSurveyExport(params);
          },
          () => {
            return `Patient Survey Export Excel_${moment().valueOf()}`;
          },
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        );
        handleClose();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectType = (type) => () => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      types: _.some(prevFormData.types, (item) => item === type)
        ? _.filter(prevFormData.types, (item) => item !== type)
        : [...prevFormData.types, type],
    }));
  };

  const handleInput = (type) => (value) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [type]: value,
    }));
  };

  const handlePasswordType = () => {
    setPasswordType((prevType) => (prevType === 'password' ? 'text' : 'password'));
  };

  const verifySuccess = useMemo(() => {
    const { isRequired, ...otherVerify } = verify;
    return (
      isRequired === 'success' &&
      _.filter(otherVerify, (item) => item === 'success').length >= 2 &&
      moment(formData.startDate).isBefore(moment(formData.endDate))
    );
  }, [formData, verify]);

  const checkRequirements = async (password) => {
    const isRequired = password.match(/^.{8,30}$/gi) !== null ? 'success' : 'fail'; // 添加8-30位字符的验证逻辑
    let tempVerify = {
      lowercaseLetters: password.match(/[a-z]/) ? 'success' : 'fail',
      uppercaseLetters: password.match(/[A-Z]/) ? 'success' : 'fail',
      numerals: password.match(/\d/) ? 'success' : 'fail',
    };
    if (Object.values(tempVerify).filter((v) => v === 'fail').length === 1) {
      tempVerify = _.mapValues(tempVerify, (value) => (value === 'fail' ? 'pending' : value));
    }
    setVerify({ ...tempVerify, isRequired });
    return Object.values(tempVerify).filter((v) => v === 'success').length >= 2 && isRequired === 'success';
  };

  return (
    <Dialog
      open={true}
      title="Export Appreciation / Feedback"
      cancel="Cancel"
      confirm="Export"
      handleClose={handleClose}
      handleCancel={handleClose}
      handleConfirm={handleExport}
      dialogfootStyle={{ marginTop: '1.88rem' }}
      disabled={!verifySuccess}
    >
      <Box display="flex" flexDirection="column">
        <Text fontSize="0.875rem" color="#666666" mb="0.75rem">
          Date
        </Text>
        <Box display="flex" mb="2.5rem">
          <CustomDateTimePicker
            datepicker={true}
            format={dateFormat}
            iconColor="#538fff"
            value={formData?.startDate}
            onChange={handleInput('startDate')}
            strictCompareDates={false}
            maxDate={formData?.endDate}
          />
          <Text mt="0.75rem" ml="0.5rem" mr="0.5rem">
            -
          </Text>
          <CustomDateTimePicker
            datepicker={true}
            format={dateFormat}
            iconColor="#538fff"
            value={formData?.endDate}
            onChange={handleInput('endDate')}
            strictCompareDates={false}
          />
        </Box>
        <Text fontSize="1rem" color="#2E4469" mb="1.25rem">
          Select Type
        </Text>
        <Box display="flex" mb="1.88rem" gridGap="0.62rem">
          <Checkbox
            check={_.some(formData.types, (item) => item === 'Appreciation')}
            handleChange={handleSelectType('Appreciation')}
          />
          <Text fontSize="1rem" color="#2E4469">
            Appreciation
          </Text>
        </Box>
        <Box display="flex" mb="1.88rem" gridGap="0.62rem">
          <Checkbox
            check={_.some(formData.types, (item) => item === 'Feedback')}
            handleChange={handleSelectType('Feedback')}
          />
          <Text fontSize="1rem" color="#2E4469">
            Feedback
          </Text>
        </Box>
        <Box display="flex" mb="2.5rem" gridGap="0.62rem">
          <Checkbox
            check={_.some(formData.types, (item) => item === 'Complaint')}
            handleChange={handleSelectType('Complaint')}
          />
          <Text fontSize="1rem" color="#2E4469">
            Complaint
          </Text>
        </Box>
        <Text fontSize="0.875rem" color="#666666" mb="0.75rem">
          Password<span style={{ fontSize: '1rem', color: '#FF0300' }}>*</span>
        </Text>
        <StyleInput
          endAdornment={
            <ButtonBase onClick={handlePasswordType}>
              <SeeIcon className={classes.seeIcon} />
            </ButtonBase>
          }
          placeholder="Password"
          inputType={passwordType}
          value={formData?.filePassword}
          onChange={handleInput('filePassword')}
          onBlur={() => checkRequirements(formData.filePassword)}
        />
        <Box
          display="flex"
          flexDirection="column"
          gridGap="1rem"
          mt="0.63rem"
          bgcolor="#F5FAFF"
          borderRadius="0.625rem"
          p="1.25rem"
        >
          <Text fontSize="1.25rem" color="#2E4469" fontWeight="700">
            Password requirements
          </Text>
          <Verify verify={verify.isRequired} verifyMsg="Must between 8-30 characters" />
          <Text fontSize="1.125rem" color="#2E4469">
            And contains at least 2 of the followings :
          </Text>
          <Verify verify={verify.uppercaseLetters} verifyMsg="Uppercase letter (A-Z)" />
          <Verify verify={verify.lowercaseLetters} verifyMsg="Lowercase letter (a-z)" />
          <Verify verify={verify.numerals} verifyMsg="Numerals (0-9)" />
        </Box>
      </Box>
    </Dialog>
  );
};

const Verify = ({ verify, verifyMsg }) => {
  const classes = useStyles();

  return (
    <Box display="flex">
      <Box display="flex" alignItems="center" width="2.75rem">
        {verify === 'pending' && (
          <Text fontWeight="700" fontSize="1.25rem" color="#2E4469" ml="0.34rem">
            -
          </Text>
        )}
        {verify === 'success' && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="1.25rem"
            height="1.25rem"
            border="solid 2px #28D2AB"
            borderRadius="50%"
          >
            <CheckIcon className={classes.checkIcon} />
          </Box>
        )}
        {verify === 'fail' && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="1.25rem"
            height="1.25rem"
            border="solid 2px #FF0000"
            borderRadius="50%"
          >
            <CloseIcon className={classes.closeIcon} />
          </Box>
        )}
      </Box>
      <Text fontSize="1.125rem" color={verify === 'fail' ? '#FF0000' : '#2E4469'}>
        {verifyMsg}
      </Text>
    </Box>
  );
};

const useStyles = makeStyles(() => ({
  seeIcon: {
    color: '#538FFF',
    fontSize: '1.5rem',
  },
  checkIcon: {
    fontSize: '0.5rem',
    color: '#28D2AB',
  },
  closeIcon: {
    fontSize: '0.5rem',
    color: '#FF0000',
  },
}));

export default ExportSurveyPopup;
